/**
 * Cartes du jour d'un paquet principal, telles que l'accueil les affiche (nouvelles, en
 * apprentissage, à revoir) : ce sont les cartes que la séance prendrait si elle démarrait
 * maintenant, limites quotidiennes comprises. Un sous-paquet compte les cartes de la liste
 * qui sont rangées dans son arborescence.
 */
import { State, type Planificateur } from '../fsrs/planificateur';
import type { ReglagesPaquet } from '../paquets/reglages';
import { aleatoireReproductible } from './aleatoire';
import {
  composerListe,
  estApprentissageInterday,
  type CarteDuPaquet,
  type RevisionPassee,
} from './composition';
import { debutJournee, finJournee } from './journee';

export interface Compteurs {
  nouvelles: number;
  apprentissage: number;
  aRevoir: number;
}

export const COMPTEURS_VIDES: Compteurs = { nouvelles: 0, apprentissage: 0, aRevoir: 0 };

export function totalCompteurs(compteurs: Compteurs): number {
  return compteurs.nouvelles + compteurs.apprentissage + compteurs.aRevoir;
}

/** Carte avec le paquet qui la contient (sous-paquet ou paquet principal). */
export type CarteRangee = CarteDuPaquet & { paquetId: string };

export interface CartesDuJour {
  /** Compteurs par paquet de l'arborescence (le paquet principal compte toute la liste). */
  parPaquet: Map<string, Compteurs>;
  /** Nombre de cartes de la liste. */
  total: number;
  /**
   * Prochaine carte due après la liste du jour : début de la journée suivante s'il reste des
   * nouvelles cartes ou des cartes acquises, sinon la prochaine étape d'apprentissage.
   * `null` si le paquet n'a aucune carte à venir.
   */
  prochaineEcheance: Date | null;
}

export function cartesDuJour(entree: {
  paquetPrincipalId: string;
  /** Parent de chaque paquet de l'arborescence (`null` pour le paquet principal). */
  parents: ReadonlyMap<string, string | null>;
  cartes: readonly CarteRangee[];
  revisions: readonly RevisionPassee[];
  reglages: ReglagesPaquet;
  planificateur: Planificateur;
  maintenant: Date;
}): CartesDuJour {
  const { reglages, maintenant } = entree;
  // Aléa fixe pour la journée : les compteurs ne changent pas d'un affichage à l'autre.
  const graine = debutJournee(maintenant, reglages.heureDebutJournee).getTime() / 3_600_000;
  const liste = composerListe({
    cartes: entree.cartes,
    revisions: entree.revisions,
    reglages,
    planificateur: entree.planificateur,
    maintenant,
    aleatoire: aleatoireReproductible(graine),
  });
  const rangement = new Map(entree.cartes.map((carte) => [carte.id, carte.paquetId]));
  const parPaquet = new Map<string, Compteurs>();
  for (const id of entree.parents.keys()) parPaquet.set(id, { ...COMPTEURS_VIDES });
  for (const carte of liste) {
    const cle: keyof Compteurs =
      carte.fsrs.state === State.New
        ? 'nouvelles'
        : carte.fsrs.state === State.Review
          ? 'aRevoir'
          : 'apprentissage';
    // Le paquet de la carte et tous ses ancêtres jusqu'au paquet principal.
    let paquet: string | null | undefined = rangement.get(carte.id);
    while (paquet !== null && paquet !== undefined) {
      const compteurs = parPaquet.get(paquet);
      if (compteurs !== undefined) compteurs[cle] += 1;
      paquet = entree.parents.get(paquet);
    }
  }

  const dansLaListe = new Set(liste.map((carte) => carte.id));
  const demain = finJournee(maintenant, reglages.heureDebutJournee);
  let prochaine: number | null = null;
  for (const carte of entree.cartes) {
    if (carte.suspendue || dansLaListe.has(carte.id)) continue;
    // Carte acquise ou apprentissage interday : due dès le début de la journée du paquet où
    // elle revient, au plus tôt demain (sinon elle serait dans la liste du jour).
    const parJournee =
      carte.fsrs.state === State.Review ||
      estApprentissageInterday(carte.fsrs, reglages.heureDebutJournee);
    const date =
      carte.fsrs.state === State.New
        ? reglages.ordreCartes === 'probabilite_rappel'
          ? null
          : demain.getTime()
        : parJournee
          ? Math.max(
              debutJournee(carte.fsrs.due, reglages.heureDebutJournee).getTime(),
              demain.getTime(),
            )
          : Math.max(carte.fsrs.due.getTime(), maintenant.getTime());
    if (date !== null && (prochaine === null || date < prochaine)) prochaine = date;
  }
  return {
    parPaquet,
    total: liste.length,
    prochaineEcheance: prochaine === null ? null : new Date(prochaine),
  };
}

/** Temps de réponse supposé tant que le paquet n'a pas assez de révisions pour une moyenne. */
export const TEMPS_DE_REPONSE_PAR_DEFAUT_MS = 10_000;

/**
 * Durée estimée de la séance, en minutes (arrondie à la minute supérieure) : temps de réponse
 * moyen enregistré × cartes du jour ; sans assez de révisions pour une moyenne utile, 10 s par
 * carte. `null` s'il n'y a aucune carte à réviser.
 */
export function dureeEstimeeMinutes(
  tempsDeReponseMs: readonly number[],
  cartes: number,
  minimum = 5,
): number | null {
  if (cartes <= 0) return null;
  const moyenne =
    tempsDeReponseMs.length < minimum
      ? TEMPS_DE_REPONSE_PAR_DEFAUT_MS
      : tempsDeReponseMs.reduce((a, b) => a + b, 0) / tempsDeReponseMs.length;
  return Math.max(1, Math.ceil((moyenne * cartes) / 60_000));
}
